/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    assigneeId,
    unassigned,
    page,
    per_page,
    labels,
    productLabels,
    topicLabels,
    concurrentLabels,
    teamId,
    conversationType,
    sortBy,
    priority,
    lastMessageBy,
    campaignFilter,
    after_datetime
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        assignee_id: assigneeId,
        unassigned: unassigned,
        status,
        assignee_type: assigneeType,
        page,
        per_page,
        labels,
        product_labels: productLabels,
        topic_labels: topicLabels,
        concurrent_labels: concurrentLabels,
        conversation_type: conversationType,
        sort_by: sortBy,
        priority,
        last_message_by: lastMessageBy,
        campaign_filter: campaignFilter && campaignFilter !== 'all' ? campaignFilter : undefined,
        after_datetime
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId, teamId = null }) {
    const payload = { assignee_id: agentId };
    if (teamId !== null) {
      payload.team_id = teamId;
    }
    return axios.post(`${this.url}/${conversationId}/assignments`, payload);
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    productLabels,
    topicLabels,
    concurrentLabels,
    teamId,
    conversationType,
    folders = false,
    onlyMetaAllCount = false,
    ignoreCache = false
  }) {
    if (folders !== false) {
      throw new Error('Ignore request meta for folders');
    }
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        product_labels: productLabels,
        topic_labels: topicLabels,
        concurrent_labels: concurrentLabels,
        team_id: teamId,
        conversation_type: conversationType,
        folders,
        onlyMetaAllCount,
        ignoreCache
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  downloadTranscriptPdf({ conversationId }) {
    return axios.get(
      `${this.url}/${conversationId}/download_transcript`,
      {
        responseType: 'blob',
      }
    );
  }

  getScheduledMessage({ conversationId }) {
    return axios.get(`${this.url}/${conversationId}/scheduled_message`);
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }
}

export default new ConversationApi();
